import { useEffect, useState } from 'react';
import { sanitizeValue } from '../../lib/sanitize';
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { useSort } from '../useSort';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addExperienceGroup'
  | 'bulkAddExperienceGroups'
  | 'getExperienceGroup'
  | 'getExperienceGroups'
  | 'updateExperienceGroup';

export const useExperienceGroups = () => {
  const [action, setAction] = useState<Action>('');
  const [bulkExperienceGroups, setBulkExperienceGroups] =
    useState<BulkExperienceGroup>();
  const [experienceGroup, setExperienceGroup] =
    useState<ExperienceGroup | null>(null);
  const [experienceGroups, setExperienceGroups] = useState<ExperienceGroup[]>(
    []
  );
  const { loading, makeRequest, response } = useFetch();
  const {
    id: operatorId,
    experienceGroups: operatorExperienceGroups,
    updateOperatorState,
  } = useOperator();
  const { sortAsc } = useSort<{ description: string }>('description');
  const BASE_URL = `${BASE_URL_MAP['Residential']}/${operatorId}/ExperienceGroups`;

  useEffect(() => {
    resetState();
  }, [operatorId]);

  useEffect(() => {
    if (response && !loading) {
      if (action === 'bulkAddExperienceGroups') {
        const bulkResponse = mapBulkResponse(response);
        setBulkExperienceGroups(bulkResponse);
        updateOperatorState('experienceGroups', [
          ...(operatorExperienceGroups ?? [])
            .concat(bulkResponse.success)
            .sort(sortAsc),
        ]);
      } else if (action === 'updateExperienceGroup') {
        const mappedExperienceGroup = mapExperienceGroup(response);
        updateOperatorState(
          'experienceGroups',
          [
            mappedExperienceGroup,
            ...(operatorExperienceGroups ?? []).filter(
              (experienceGroup) =>
                experienceGroup.id !== mappedExperienceGroup.id
            ),
          ].sort(sortAsc)
        );
      } else if (action === 'addExperienceGroup') {
        const mappedExperienceGroup = mapExperienceGroup(response);

        updateOperatorState(
          'experienceGroups',
          [mappedExperienceGroup, ...(operatorExperienceGroups ?? [])].sort(
            sortAsc
          )
        );
      } else if (action === 'getExperienceGroup')
        setExperienceGroup(mapExperienceGroup(response));
      else if (action === 'getExperienceGroups')
        setExperienceGroups(
          response.map((experienceGroup: ApiExperienceGroup) =>
            mapExperienceGroup(experienceGroup)
          )
        );
    }
    // eslint-disable-next-line
  }, [loading, response]);

  const addExperienceGroup = (experienceGroup: CreateExperienceGroup) => {
    setAction('addExperienceGroup');
    const body = mapApiExperienceGroup(experienceGroup);

    makeRequest(BASE_URL, 'post', sanitizeValue(body));
  };

  const bulkAddExperienceGroups = (
    experienceGroups: CreateExperienceGroup[]
  ) => {
    setAction('bulkAddExperienceGroups');
    const body = experienceGroups.map((experienceGroup) =>
      mapApiExperienceGroup(experienceGroup)
    );

    makeRequest(`${BASE_URL}/BulkAdd`, 'post', sanitizeValue(body));
  };

  const clearBulkAdds = () =>
    setBulkExperienceGroups({ success: [], fail: [] });

  const getExperienceGroup = (experienceGroupId: string) => {
    setAction('getExperienceGroup');
    makeRequest(`${BASE_URL}/${experienceGroupId}`, 'get');
  };

  const getExperienceGroups = () => {
    setAction('getExperienceGroups');
    makeRequest(BASE_URL, 'get');
  };

  const updateExperienceGroup = (experienceGroup: ExperienceGroup) => {
    setAction('updateExperienceGroup');
    const body: ApiCreateExperienceGroup = {
      Active: experienceGroup.active,
      Description: experienceGroup.description,
      MediaFirstID: experienceGroup.mediafirstId,
    };

    makeRequest(
      `${BASE_URL}/${experienceGroup.id}`,
      'put',
      sanitizeValue(body)
    );
  };

  const resetState = () => {
    setAction('');
    setBulkExperienceGroups({ success: [], fail: [] });
    setExperienceGroup(null);
    setExperienceGroups([]);
  };

  return {
    addExperienceGroup,
    experienceGroup,
    experienceGroups,
    bulkAddExperienceGroups,
    bulkExperienceGroups,
    clearBulkAdds,
    getExperienceGroup,
    getExperienceGroups,
    loading,
    updateExperienceGroup,
  };
};

const mapApiExperienceGroup = (
  experienceGroup: CreateExperienceGroup
): ApiCreateExperienceGroup => ({
  Active: experienceGroup.active,
  Description: experienceGroup.description,
  MediaFirstID: experienceGroup.mediafirstId,
});

const mapBulkResponse = (
  response: ApiBulkResponse<ApiExperienceGroup, ApiCreateExperienceGroup>
): BulkExperienceGroup => ({
  success: response.Success.map((experienceGroup) =>
    mapExperienceGroup(experienceGroup)
  ),
  fail: response.Fail.map((experienceGroup) => ({
    active: experienceGroup.Active,
    description: experienceGroup.Description,
    mediafirstId: experienceGroup.MediaFirstID,
  })),
});

export const mapExperienceGroup = (
  experienceGroup: ApiExperienceGroup
): ExperienceGroup => ({
  id: experienceGroup.ID,
  active: experienceGroup.Active,
  description: experienceGroup.Description,
  mediafirstId: experienceGroup.MediaFirstID,
});
